"use client";
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LuCalendarDays } from "react-icons/lu";
import { IoMdTime } from "react-icons/io";
import { GoPlus, GoTrash } from "react-icons/go";
import { IoClose } from "react-icons/io5";
import toast from "react-hot-toast";
import axios from "axios";
import "./EditClientModalCalendar.css";
import { REST_API_URL } from "../api/config";
import { buildHeaders } from "../api/buildHeaders";
import { getCurrencySymbol } from "../utils/currency/getCurrencySymbol";
import { PrimaryButton } from "../components/buttons/primaryButton/PrimaryButton.tsx";
import { useTranslation } from "react-i18next";

const API_BASE = REST_API_URL;

/* ─── helpers ─── */
const parseTimeString = (timeString) => {
  if (!timeString) return { hour: 8, minute: 0, ampm: "AM" };
  try {
    if (typeof timeString === "string" && timeString.includes(" ")) {
      const [timePart, ampm] = timeString.split(" ");
      const [hour, minute] = timePart.split(":").map(Number);
      let hour24 = hour;
      if (ampm === "PM" && hour !== 12) hour24 = hour + 12;
      if (ampm === "AM" && hour === 12) hour24 = 0;
      return { hour: hour24, minute: minute || 0, ampm };
    }
    if (typeof timeString === "string" && timeString.includes(":")) {
      const [hour, minute] = timeString.split(":").map(Number);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      return { hour, minute: minute || 0, ampm, hour12 };
    }
    const date = new Date(timeString);
    if (!isNaN(date.getTime())) {
      const hour = date.getHours();
      const minute = date.getMinutes();
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      return { hour, minute, ampm, hour12 };
    }
    return { hour: 8, minute: 0, ampm: "AM" };
  } catch {
    return { hour: 8, minute: 0, ampm: "AM" };
  }
};

const formatTimeForDisplay = (hour, minute, ampm) => {
  const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${ampm}`;
};

const calculateDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return { hours: 1, minutes: 0 };
  try {
    const start = parseTimeString(startTime);
    const end = parseTimeString(endTime);
    let diff = end.hour * 60 + end.minute - (start.hour * 60 + start.minute);
    if (diff < 0) diff += 24 * 60;
    return { hours: Math.floor(diff / 60), minutes: diff % 60 };
  } catch {
    return { hours: 1, minutes: 0 };
  }
};

const formatPriceWithCurrency = (price, currency, freeLabel = "Free") => {
  const num = parseFloat(price || 0);
  const symbol = getCurrencySymbol(currency || "USD");
  if (num === 0) return freeLabel;
  return `${symbol}${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const truncateText = (text, max) => {
  if (!text) return "";
  return text.length > max ? text.slice(0, max) + "…" : text;
};

/* ═══════════════════════════════════════════════
   MAIN MODAL
═══════════════════════════════════════════════ */
export default function EditClientModalCalendar({
  isOpen,
  onClose,
  event,
  onSaveSuccess,
  userCurrency,
}) {
  const { t } = useTranslation();
  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  const overlayRef = useRef(null);

  const [formData, setFormData] = useState({
    date: "",
    time: "08:00 AM",
    duration: { hours: 1, minutes: 0 },
    services: [],
  });
  const [availableServices, setAvailableServices] = useState([]);
  // null = no draft row; otherwise id of the service selected in the draft row
  const [draftServiceId, setDraftServiceId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  // 412/414 → confirm modal (resend with confirmed:true); 410 → error modal
  const [confirmModal, setConfirmModal] = useState({ open: false, message: "" });
  const [errorModal, setErrorModal] = useState({ open: false, message: "" });
  const [userStatus, setUserStatus] = useState(null);
  const [userCurrencyCode, setUserCurrencyCode] = useState(
    userCurrency || "USD",
  );

  /* close on overlay click */
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  /* fetch user info + services */
  const fetchAllData = async () => {
    if (!token) return;
    try {
      const userRes = await axios.get(`${API_BASE}/auth/userinfo/`, {
        headers: buildHeaders(),
      });
      const userData = userRes.data;
      setUserStatus(userData?.user?.status || null);
      const currency = userData?.currency || "USD";
      setUserCurrencyCode(currency);
    } catch (err) {
      console.error("Error fetching user info:", err);
    }
  };

  const fetchServices = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_BASE}/service/my-services/`, {
        headers: buildHeaders(),
      });
      if (res.status === 200) {
        const list = Array.isArray(res.data) ? res.data : [];
        setAvailableServices(
          list.map((s) => ({
            ...s,
            currency: s.currency || userCurrencyCode,
            currencySymbol: getCurrencySymbol(s.currency || userCurrencyCode),
          })),
        );
      }
    } catch (err) {
      console.error("Failed to fetch services:", err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAllData();
      fetchServices();
    }
  }, [isOpen]);
  useEffect(() => {
    if (userCurrency) setUserCurrencyCode(userCurrency);
  }, [userCurrency]);

  /* populate form from event */
  useEffect(() => {
    if (!event || !isOpen) return;

    let formattedDate = "";
    if (event.date) {
      try {
        const dateStr = event.date;
        let dateObj;
        if (dateStr.includes("T")) dateObj = new Date(dateStr);
        else if (dateStr.includes("-")) {
          const [y, m, d] = dateStr.split("-").map(Number);
          dateObj = new Date(y, m - 1, d);
        } else dateObj = new Date(dateStr);
        if (!isNaN(dateObj.getTime())) {
          const monthKeys = [
            "month.january",
            "month.february",
            "month.march",
            "month.april",
            "month.may",
            "month.june",
            "month.july",
            "month.august",
            "month.september",
            "month.october",
            "month.november",
            "month.december",
          ];
          formattedDate = `${t(monthKeys[dateObj.getMonth()])} ${dateObj.getDate()}`;
        }
      } catch {}
    }

    const startTime = event.start_time || event.timeStart || event.timeStart24h;
    const endTime = event.end_time || event.timeEnd || event.timeEnd24h;
    let formattedTime = "08:00 AM";
    if (startTime) {
      try {
        const p = parseTimeString(startTime);
        formattedTime = formatTimeForDisplay(p.hour, p.minute, p.ampm);
      } catch {}
    }
    let durationObj = { hours: 1, minutes: 0 };
    if (startTime && endTime) {
      try {
        durationObj = calculateDuration(startTime, endTime);
      } catch {}
    }

    // ── Строим список сервисов как в мобильной версии:
    // [ основной сервис, ...added_services ]
    const mainService = event.service
      ? {
          id: event.service_id || null,
          name: event.service,
          price: event.servicePrice || 0,
          duration: event.duration || null,
          currency: event.currency || userCurrencyCode,
          currencySymbol: getCurrencySymbol(event.currency || userCurrencyCode),
          isMain: true,
        }
      : null;

    const addedServices = Array.isArray(event.added_services)
      ? event.added_services.map((s) => ({
          ...s,
          currency: s.currency || event.currency || userCurrencyCode,
          currencySymbol: getCurrencySymbol(
            s.currency || event.currency || userCurrencyCode,
          ),
          isMain: false,
        }))
      : [];

    // Если есть event.services (другой формат API) — используем его
    let parsedServices;
    if (Array.isArray(event.services) && event.services.length > 0) {
      parsedServices = event.services.map((s) => ({
        ...s,
        currency: s.currency || event.currency || userCurrencyCode,
        currencySymbol: getCurrencySymbol(
          s.currency || event.currency || userCurrencyCode,
        ),
      }));
    } else {
      parsedServices = [
        ...(mainService ? [mainService] : []),
        ...addedServices,
      ];
    }

    setFormData({
      date: formattedDate,
      time: formattedTime,
      duration: durationObj,
      services: parsedServices,
    });
  }, [event, isOpen, userCurrencyCode, t]);

  useEffect(() => {
    setDraftServiceId(null);
  }, [event?.id, isOpen]);

  /* service actions */
  // service_id уже добавленных сервисов (основной + added)
  const getUsedServiceIds = () =>
    new Set(
      formData.services.map((s) => String(s.service_id ?? s.service ?? s.id)),
    );

  // доступные для добавления = все минус уже добавленные (по service_id)
  const getSelectableServices = () => {
    const used = getUsedServiceIds();
    return availableServices.filter((s) => !used.has(String(s.id)));
  };

  // "+" only opens a local draft row — nothing is sent to the backend yet
  const handleAddService = () => {
    if (!event?.id) return toast.error(t("calendar.toastNoAppointmentId"));
    const selectable = getSelectableServices();
    if (!selectable.length)
      return toast.error(t("calendar.toastNoServicesAvailable"));
    setDraftServiceId(selectable[0].id);
  };

  const handleCancelDraft = () => setDraftServiceId(null);

  // "✓" confirms the draft and sends it to the backend
  const handleConfirmDraft = () => submitAddService(false);

  // отправка add-service; confirmed=true при подтверждении модалки (412/414)
  const submitAddService = async (confirmed) => {
    if (savingDraft) return;
    const newService = availableServices.find(
      (s) => String(s.id) === String(draftServiceId),
    );
    if (!newService) return;
    if (!event?.id) return toast.error(t("calendar.toastNoAppointmentId"));
    const serviceCurrency = newService.currency || userCurrencyCode;
    try {
      setSavingDraft(true);
      const res = await axios.post(
        `${API_BASE}/client/appointments/${event.id}/add-service/`,
        {
          service: newService.id,
          duration: 30,
          time_unit: "minutes",
          price: newService.price,
          currency: serviceCurrency,
          ...(confirmed && { confirmed: true }),
        },
        { headers: buildHeaders() },
      );
      const addedService = {
        ...newService,
        id: res.data?.id || newService.id,
        service_id: newService.id,
        currency: serviceCurrency,
        currencySymbol: getCurrencySymbol(serviceCurrency),
      };
      setFormData((prev) => ({
        ...prev,
        services: [...prev.services, addedService],
      }));
      setDraftServiceId(null);
      setConfirmModal({ open: false, message: "" });
      toast.success(t("calendar.toastServiceAdded", { name: newService.name }));
    } catch (err) {
      console.error("Add service failed:", err);
      const code = err.response?.data?.code;
      const message = err.response?.data?.message;
      if (code === 412 || code === 414) {
        setConfirmModal({ open: true, message: message || "" });
      } else if (code === 410) {
        setErrorModal({ open: true, message: message || "" });
      } else {
        toast.error(message || t("calendar.toastFailedAddService"));
      }
    } finally {
      setSavingDraft(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!event?.id) return toast.error(t("calendar.toastNoAppointmentId"));
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s.id !== serviceId),
    }));
    try {
      await axios.delete(
        `${API_BASE}/client/added-services/${serviceId}/delete/`,
        { headers: buildHeaders() },
      );
      toast.success(t("calendar.toastServiceRemoved"));
    } catch (err) {
      console.error("Delete service failed:", err);
      toast.error(t("calendar.toastFailedDeleteService"));
    }
  };

  const handleCancelAppointment = async () => {
    if (!event?.id) return toast.error(t("calendar.toastNoAppointmentId"));
    try {
      await axios.post(
        `${API_BASE}/client/appointment-cancel/`,
        { id: event.id },
        { headers: buildHeaders() },
      );
      toast.success(t("calendar.toastAppointmentCanceled"));
      onClose();
      if (typeof onSaveSuccess === "function") onSaveSuccess();
    } catch (err) {
      console.error("Cancel failed:", err);
      toast.error(
        err.response?.data?.detail ||
          t("calendar.toastFailedCancelAppointment"),
      );
    }
  };

  /* ── services list ──
   * Точно как в мобильной версии (ClientDetailsBottomSheet):
   * const services = [ { service, price, duration, currency: null }, ...(clientDetails.added_services ?? []) ]
   */
  const services =
    formData.services.length > 0
      ? formData.services
      : [
          ...(event?.service
            ? [
                {
                  id: event.service_id || event.id,
                  name: event.service,
                  price: event.servicePrice || 0,
                  currency: event.currency || userCurrencyCode,
                  isMain: true,
                },
              ]
            : []),
          ...(Array.isArray(event?.added_services)
            ? event.added_services.map((s) => ({
                ...s,
                currency: s.currency || event?.currency || userCurrencyCode,
                isMain: false,
              }))
            : []),
        ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={overlayRef}
          className="ecm-overlay"
          onClick={handleOverlayClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="ecm-sheet"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
          >
            {/* Header */}
            <div className="ecm-header">
              <h2 className="ecm-title">{t("calendar.details")}</h2>
              <PrimaryButton
                onClick={onClose}
                label={<IoClose />}
                variant="ghost"
              />
            </div>

            {/* ── scrollable body ── */}
            <div className="ecm-body">
              {/* google event sub-title */}
              {event?.isGoogle && event?.titleEvent && (
                <p className="ecm-subtitle">
                  {truncateText(event.titleEvent, 30)}
                </p>
              )}

              {/* avatar */}
              <img
                className="ecm-avatar"
                src={
                  event?.isGoogle
                    ? "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Google_Calendar_icon_%282020%29.svg/120px-Google_Calendar_icon_%282020%29.svg.png"
                    : event?.avatar || "/default-avatar.png"
                }
                alt={t("home.clientAvatarAlt")}
              />

              {/* ── Date & Time label ── */}
              <p className="ecm-section-label">{t("calendar.dateAndTime")}</p>
              <div className="ecm-date-time-row">
                {/* date card */}
                <div className="ecm-info-card">
                  <LuCalendarDays className="ecm-info-icon" />
                  <span>{formData.date || event?.date || "—"}</span>
                </div>
                {/* time card */}
                <div className="ecm-info-card">
                  <IoMdTime className="ecm-info-icon" />
                  <span>{formData.time || event?.time || "—"}</span>
                </div>
              </div>

              {/* ── Service label ── */}
              <p className="ecm-section-label">
                {event?.isGoogle
                  ? t("calendar.description")
                  : t("calendar.service")}
              </p>

              {event?.isGoogle ? (
                <p className="ecm-description-text">
                  {event?.description || "—"}
                </p>
              ) : (
                <div className="ecm-services-list">
                  {services.map((service, index) => (
                    <div key={service.id || index} className="ecm-service-row">
                      {/* service name + price */}
                      <div className="ecm-service-card">
                        <span className="ecm-service-name">
                          {truncateText(service.name || service.service, 22)}
                        </span>
                        <span className="ecm-service-price">
                          {formatPriceWithCurrency(
                            service.price,
                            service.currency,
                            t("calendar.free"),
                          )}
                        </span>
                      </div>

                      {index === 0 && event?.status !== "Canceled" && event.type === "service" ? (
                        <button
                          className="ecm-action-btn ecm-action-btn--add"
                          onClick={handleAddService}
                          title={t("calendar.addService")}
                        >
                          <GoPlus />
                        </button>
                      ) : index !== 0 ? (
                        <button
                          className="ecm-action-btn ecm-action-btn--del"
                          onClick={() => handleDeleteService(service.id)}
                          title={t("calendar.remove")}
                        >
                          <GoTrash />
                        </button>
                      ) : null}

                    </div>
                  ))}

                  {/* draft row: pick a service, nothing is sent until ✓ */}
                  {draftServiceId !== null && (
                    <div className="ecm-service-row">
                      <div className="ecm-service-card">
                        <select
                          className="ecm-service-select"
                          value={draftServiceId}
                          disabled={savingDraft}
                          onChange={(e) => setDraftServiceId(e.target.value)}
                        >
                          {getSelectableServices().map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name} —{" "}
                              {formatPriceWithCurrency(
                                s.price,
                                s.currency,
                                t("calendar.free"),
                              )}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        className="ecm-action-btn"
                        onClick={handleConfirmDraft}
                        disabled={savingDraft}
                        title={t("confirm")}
                      >
                        ✓
                      </button>
                      <button
                        className="ecm-action-btn ecm-action-btn--del"
                        onClick={handleCancelDraft}
                        disabled={savingDraft}
                        title={t("cancel")}
                      >
                        <IoClose />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ── Master / worker (salon only) ── */}
              {userStatus === "salon" &&
                (event?.master || event?.worker_avatar) && (
                  <>
                    <p className="ecm-section-label">{t("calendar.master")}</p>
                    <div className="ecm-master-card">
                      {event?.worker_avatar && (
                        <img
                          className="ecm-master-avatar"
                          src={event.worker_avatar}
                          alt={t("calendar.master")}
                        />
                      )}
                      <div>
                        <span className="ecm-master-name">
                          {truncateText(
                            event?.master || event?.full_name || event?.email,
                            22,
                          )}
                        </span>
                      </div>
                    </div>
                  </>
                )}

              {/* ── Meet link ── */}
              {event?.meet_link && (
                <>
                  <p className="ecm-section-label">{t("calendar.meetLink")}</p>
                  <a
                    className="ecm-meet-link"
                    href={event.meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    🎥 {truncateText(event.meet_link, 40)}
                  </a>
                </>
              )}

              {/* ── Buttons ── */}
              <PrimaryButton
                onClick={onClose}
                label={t("calendar.back")}
                className="mt-3"
              />

              {event?.status !== "Canceled" && userStatus !== "worker" && (
                <PrimaryButton
                  className="ecm-btn-cancel-appt"
                  onClick={handleCancelAppointment}
                  label={
                    event?.isGoogle
                      ? t("calendar.cancelEvent")
                      : t("calendar.cancelAppointment")
                  }
                />
              )}
            </div>

            {/* 412/414 — подтверждение (повторная отправка с confirmed:true) */}
            {confirmModal.open && (
              <div className="ecm-modal-overlay">
                <div className="ecm-modal">
                  <p className="ecm-modal-text">{confirmModal.message}</p>
                  <div className="ecm-modal-buttons">
                    <PrimaryButton
                      variant="ghost"
                      label={t("cancel")}
                      disabled={savingDraft}
                      onClick={() =>
                        setConfirmModal({ open: false, message: "" })
                      }
                    />
                    <PrimaryButton
                      label={t("confirm")}
                      disabled={savingDraft}
                      onClick={() => submitAddService(true)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 410 — ошибка (только OK) */}
            {errorModal.open && (
              <div className="ecm-modal-overlay">
                <div className="ecm-modal">
                  <p className="ecm-modal-text">{errorModal.message}</p>
                  <div className="ecm-modal-buttons">
                    <PrimaryButton
                      label={t("ok")}
                      onClick={() =>
                        setErrorModal({ open: false, message: "" })
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
